import { apiGet, apiPost } from '.'

export interface MessageIdentity {
  id: number
  display_name: string
  platform: string
}

export interface MessageContact {
  id: number
  name: string
  location: string | null
}

export interface MessageSentBy {
  id: number
  display_name: string
}

export interface Message {
  id: number
  station_id: number
  contact_id: number
  contact_identity_id: number
  platform: string
  content: string | null
  content_type: string
  intent: string | null
  received_at: string
  received_at_iso_utc?: string | null
  received_at_epoch?: number | null
  is_read: boolean
  is_flagged: boolean
  is_on_air: boolean
  is_autoresponder: boolean
  round_id: number | null
  reply_text?: string | null
  reply_at?: string | null
  reply_at_iso_utc?: string | null
  reply_at_epoch?: number | null
  direction: 'in' | 'out'
  reply_to_message_id: number | null
  sent_by_user_id: number | null
  transport_status: 'queued' | 'sent' | 'failed' | null
  transport_error?: string | null
  auto_rule_id: number | null
  auto_rule_name: string | null
  contact: MessageContact
  identity: MessageIdentity
  sent_by?: MessageSentBy | null
}

export interface ConversationMeta {
  contact: MessageContact
  identities: Array<MessageIdentity>
  active_identity: number | null
  blocked: {
    contact: boolean
    identity: boolean
  }
}

export interface MessagesResponse {
  items: Message[]
  next_cursor: number | null
  prev_cursor: number | null
  total_unread: number
  conversation?: ConversationMeta
  first_unread_id?: number | null
}

export interface ListMessagesParams {
  stationId: number
  cursor?: number | null
  dir?: 'older' | 'newer'
  limit?: number
  unreadOnly?: boolean
  flaggedOnly?: boolean
  onAirOnly?: boolean
  platforms?: string[]
  intents?: string[]
  contactId?: number | null
  identityId?: number | null
  includeBlocked?: boolean
  firstUnread?: boolean
}

const toCsv = (values?: string[]): string | undefined => {
  if (!values || !values.length) {
    return undefined
  }
  return values.join(',')
}

export async function listMessages(
  params: ListMessagesParams,
  signal?: AbortSignal,
): Promise<MessagesResponse> {
  const {
    stationId,
    cursor,
    dir,
    limit,
    unreadOnly,
    flaggedOnly,
    onAirOnly,
    platforms,
    intents,
    contactId,
    identityId,
    includeBlocked,
    firstUnread,
  } = params

  const search = new URLSearchParams()
  search.set('station_id', String(stationId))

  if (cursor) {
    search.set('cursor', String(cursor))
  }
  if (dir) {
    search.set('dir', dir)
  }
  if (limit) {
    search.set('limit', String(limit))
  }
  if (unreadOnly) {
    search.set('filter_unread', '1')
  }
  if (flaggedOnly) {
    search.set('filter_flagged', '1')
  }
  if (onAirOnly) {
    search.set('filter_on_air', '1')
  }

  const platformCsv = toCsv(platforms)
  if (platformCsv) {
    search.set('filter_platform', platformCsv)
  }

  const intentsCsv = toCsv(intents)
  if (intentsCsv) {
    search.set('filter_intent', intentsCsv)
  }

  if (contactId) {
    search.set('contact_id', String(contactId))
  }

  if (identityId) {
    search.set('identity_id', String(identityId))
  }

  if (includeBlocked) {
    search.set('include_blocked', '1')
  }

  if (firstUnread) {
    search.set('first_unread', '1')
  }

  const query = search.toString()
  const path = query ? `messages?${query}` : 'messages'

  if (typeof window !== 'undefined' && window.__RPA_DEBUG?.net) {
    console.log('[messages] list', {
      stationId,
      cursor,
      dir,
      limit,
      unreadOnly,
      flaggedOnly,
      onAirOnly,
      platforms,
      intents,
      contactId,
      identityId,
      includeBlocked,
      firstUnread,
    })
  }

  return apiGet<MessagesResponse>(path, { signal, cache: 'no-store' })
}

export async function markRead(id: number, stationId: number, isRead: boolean): Promise<{ ok: boolean; is_read: boolean }> {
  return apiPost<{ ok: boolean; is_read: boolean }>(`messages/${id}/read`, {
    station_id: stationId,
    is_read: isRead ? 1 : 0,
  })
}

export async function markReadBulk(
  stationId: number,
  contactId: number,
  upToId?: number,
): Promise<{ ok: boolean; affected: number }> {
  const payload: Record<string, number> = {
    station_id: stationId,
    contact_id: contactId,
  }

  if (typeof upToId === 'number' && upToId > 0) {
    payload.up_to_message_id = upToId
  }

  return apiPost<{ ok: boolean; affected: number }>('messages/read-bulk', payload)
}

export interface SendReplyResponse {
  ok: boolean
  id: number
  status: 'queued' | 'sent' | 'failed'
  identity_id: number
  text: string
  error?: string | null
  sent_by?: MessageSentBy | null
  message?: Message | null
}

export async function sendReplyByMessage(
  messageId: number,
  stationId: number,
  text: string,
  identityId?: number | null,
): Promise<SendReplyResponse> {
  const payload: Record<string, unknown> = {
    station_id: stationId,
    text,
  }

  if (identityId) {
    payload.identity_id = identityId
  }

  return apiPost<SendReplyResponse>(`messages/${messageId}/reply`, payload)
}

export async function sendReplyByContact(
  contactId: number,
  stationId: number,
  text: string,
  identityId?: number | null,
): Promise<SendReplyResponse> {
  const payload: Record<string, unknown> = {
    station_id: stationId,
    contact_id: contactId,
    text,
  }

  if (identityId) {
    payload.identity_id = identityId
  }

  return apiPost<SendReplyResponse>('messages/reply', payload)
}

